// Import react elements
import React, { useEffect, useState } from "react";

// Import components
import CreateButton from "global/components/CreateButton";
import TwoColumnList from "global/components/TwoColumnList";
import ToggleSwitch from "global/components/ToggleSwitch";
import { ColorPickerSmall } from "global/components/ColourPicker";
import { Grid } from "global/components/Grid";
import NumberStepperInput from "global/components/NumberStepperInput";



// Convert colourway settings object into editable key/value rows
const convertSettingsToRows = (settings) => {

    // If there are no settings, return an empty row list
    if (!settings) return [];

    // Convert the settings object into key/value rows
    return Object.keys(settings).map((settingKey, index) => {

        // Return the row object used by the settings editor
        return {
            id: index + 1,
            key: settingKey,
            value: settings[settingKey]
        };

    });

};



// Convert editable key/value rows back into the settings object expected by the API
const convertRowsToSettings = (settingsRows) => {

    // Create the object that will be sent to the API
    const settings = {};

    // Loop over each editable setting row
    for (let row of settingsRows) {

        // Trim the setting key
        const settingKey = String(row.key || "").trim();

        // Trim the setting value
        const settingValue = String(row.value || "").trim();

        // If both fields are blank, ignore the row
        if (!settingKey && !settingValue) continue;

        // If only one field is filled in, return a validation error
        if (!settingKey || !settingValue) {

            // Return a failed conversion result
            return {
                success: false,
                error_message: "Each setting must have both a key and a value"
            };

        }

        // Add this setting to the settings object
        settings[settingKey] = settingValue;

    }

    // If no settings were added, return null so the API stores empty settings correctly
    if (Object.keys(settings).length === 0) {

        // Return a successful empty settings result
        return {
            success: true,
            settings: null
        };

    }

    // Return the completed settings object
    return {
        success: true,
        settings: settings
    };

};



// Render add/edit colourway pop up
const RenderColourwayPopup = ({
    isOpen,
    onClose,
    colourway,
    handleSaveColourway,
    handleDeleteColourway
}) => {

    // Store the colourway form values locally so this popup does not affect the main product save state
    const [formValues, setFormValues] = useState({
        code: "",
        stock_code: "",
        core: "N",
        extra_lead_time_min: 0,
        extra_lead_time_max: 1,
        stock_used: "",
        name: "",
        hex_colour: "#000000",
        ordering: "",
        active: "Y"
    });

    // Store settings as editable rows so the UI matches MMM settings
    const [settingsRows, setSettingsRows] = useState([]);

    // Store local validation errors for the popup
    const [errorMessage, setErrorMessage] = useState("");

    // Work out whether this popup is adding or editing a colourway
    const isEditing = Boolean(colourway);

    // Load the selected colourway into the popup when it opens
    useEffect(() => {

        // If the popup is not open, do nothing
        if (!isOpen) return;

        // Clear any previous validation error when opening
        setErrorMessage("");

        // If we are editing, populate the form from the selected colourway
        if (colourway) {

            // Set the editable colourway fields
            setFormValues({
                code: colourway.code || "",
                stock_code: colourway.stock_code || "",
                core: colourway.core || "N",
                extra_lead_time_min: colourway.extra_lead_time_min || 1,
                extra_lead_time_max: colourway.extra_lead_time_max || 2,
                stock_used: colourway.stock_used || "",
                name: colourway.name || "",
                hex_colour: colourway.hex_colour || "#000000",
                ordering: colourway.ordering || "",
                active: colourway.active || "Y"
            });

            // Set the editable settings rows
            setSettingsRows(convertSettingsToRows(colourway.settings));

        } else {

            // Set the default values for a new colourway
            setFormValues({
                code: "",
                stock_code: "",
                core: "N",
                extra_lead_time_min: 1,
                extra_lead_time_max: 2,
                stock_used: "",
                name: "",
                hex_colour: "#000000",
                ordering: "",
                active: "Y"
            });

            // New colourways start with no settings
            setSettingsRows([]);

        }

    }, [isOpen, colourway]);

    // If isOpen is false return early and do not show the pop up
    if (!isOpen) return null;

    // Update a normal colourway form field
    const handleFieldChange = (event) => {

        // Get the input name
        const fieldName = event.target.name;

        // Get the input value
        const fieldValue = event.target.value;

        // Update the field in local form state
        setFormValues((previousValues) => ({
            ...previousValues,
            [fieldName]: fieldValue
        }));

    };

    // Update the colour picker value
    const handleColourChange = (hexColour) => {

        // Update the hex colour in local form state
        setFormValues((previousValues) => ({
            ...previousValues,
            hex_colour: hexColour || ""
        }));

    };

    // Update the active toggle value
    const handleActiveChange = (event) => {

        // Convert the toggle value back to the API Y/N format
        const activeValue = event.target.value ? "Y" : "N";

        // Update the active value in local form state
        setFormValues((previousValues) => ({
            ...previousValues,
            active: activeValue
        }));

    };

    // Update the core toggle value
    const handleCoreChange = (event) => {

        // Convert the toggle value back to the API Y/N format
        const coreValue = event.target.value ? "Y" : "N";

        // Update the core value in local form state
        setFormValues((previousValues) => ({
            ...previousValues,
            core: coreValue
        }));

    };

    // Update one settings row field
    const handleSettingChange = (rowId, fieldName, fieldValue) => {

        // Update the matching settings row
        setSettingsRows((previousRows) => previousRows.map((row) => {

            // If this is not the row being edited, return it unchanged
            if (row.id !== rowId) return row;

            // Return the changed settings row
            return {
                ...row,
                [fieldName]: fieldValue
            };

        }));

    };

    // Add one new empty settings row
    const handleAddSettingRow = () => {

        // Add a new blank settings row
        setSettingsRows((previousRows) => ([
            ...previousRows,
            {
                id: Date.now(),
                key: "",
                value: ""
            }
        ]));

    };

    // Remove a settings row
    const handleRemoveSettingRow = (rowId) => {

        // Remove the selected settings row
        setSettingsRows((previousRows) => previousRows.filter((row) => row.id !== rowId));

    };

    // Save the colourway from the popup
    const handleSubmit = (event) => {

        // Prevent the browser's default form submission
        event.preventDefault();

        // Clear any previous validation error
        setErrorMessage("");

        // Validate the colourway code
        if (!String(formValues.code || "").trim()) {

            // Show a validation error
            setErrorMessage("Colourway code is required");

            // Stop before saving
            return;

        }

        // Validate the colourway name
        if (!String(formValues.name || "").trim()) {

            // Show a validation error
            setErrorMessage("Colourway name is required");

            // Stop before saving
            return;

        }

        // Validate the hex colour format
        if (!/^#[0-9A-Fa-f]{6}$/.test(String(formValues.hex_colour || ""))) {

            // Show a validation error
            setErrorMessage("Hex colour must be in #RRGGBB format");

            // Stop before saving
            return;

        }

        // Convert the settings rows to the object shape expected by the API
        const settingsResult = convertRowsToSettings(settingsRows);

        // If settings conversion failed, show the validation error
        if (!settingsResult.success) {

            // Show the settings validation error
            setErrorMessage(settingsResult.error_message);

            // Stop before saving
            return;

        }

        // Build the payload for the add/update call
        const payload = {
            code: String(formValues.code || "").trim(),
            stock_code: String(formValues.stock_code || "").trim(),
            core: formValues.core === "Y" ? "Y" : "N",
            extra_lead_time_min: formValues.extra_lead_time_min || 1,
            extra_lead_time_max: formValues.extra_lead_time_max || 2,
            stock_used: String(formValues.stock_used || "").trim(),
            name: String(formValues.name || "").trim(),
            hex_colour: String(formValues.hex_colour || "").trim().toUpperCase(),
            active: formValues.active === "Y" ? "Y" : "N"
        };

        // Only send ordering if a value has been entered
        if (String(formValues.ordering || "").trim() !== "") {

            // Add ordering to the payload
            payload.ordering = Number(formValues.ordering);

        }

        // Only send settings if the editor returned settings
        if (settingsResult.settings) {

            // Add settings to the payload
            payload.settings = settingsResult.settings;

        }

        // If editing had settings and they have all been removed, send null to clear them
        if (isEditing && !settingsResult.settings) {

            // Explicitly clear settings on the existing colourway
            payload.settings = null;

        }

        // Pass the completed payload back to the parent component
        handleSaveColourway(payload);

    };

    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--small colourway-popup" style={{height: "auto"}}>

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            {isEditing ? "Edit Colourway" : "Add Colourway"}

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        <ToggleSwitch
                            name="colourway_active"
                            value={formValues.active === "Y"}
                            onChange={handleActiveChange}
                            label=""
                            labelPosition="right"
                            size="m"
                        />

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                            🞬
                        </button>

                    </div>

                </div>

                <div className="popup-content popup-content__body">

                    <Grid className={"popup_grid-container"} >

                        <form onSubmit={handleSubmit}>

                            <TwoColumnList
                                property={<label>Code</label>}
                                value={<input
                                    name="code"
                                    type="text"
                                    placeholder="Code"
                                    value={formValues.code}
                                    onChange={handleFieldChange}
                                    required
                                />}
                            />

                            <TwoColumnList
                                property={<label>Stock Code</label>}
                                value={<input
                                    name="stock_code"
                                    type="text"
                                    placeholder="Stock Code"
                                    value={formValues.stock_code}
                                    onChange={handleFieldChange}
                                />}
                            />

                            <TwoColumnList
                                property={<label>Core Stock</label>}
                                value={<ToggleSwitch
                                    name="core"
                                    value={formValues.core === "Y"}
                                    onChange={handleCoreChange}
                                    label=""
                                    labelPosition="right"
                                    size="s" 
                                />}
                                valueIsToggle={true}
                            />

                            {formValues.core === "N" && 
                            
                                <>
                                
                                    <TwoColumnList
                                        property={<label>Min. Lead Time</label>}
                                        value={
                                            <NumberStepperInput
                                                name="extra_lead_time_min"
                                                ariaLabelMinus="Decrease minimum lead time"
                                                ariaLabelPlus="Increase minimum lead time"
                                                step={1}
                                                min={0}
                                                value={formValues.extra_lead_time_min}
                                                onChange={handleFieldChange}
                                                placeholder="1"
                                                rightLabel="days"
                                            />
                                        }
                                        valueIsNumberStepper={true}
                                    /> 

                                    <TwoColumnList
                                        property={<label>Max. Lead Time</label>}
                                        value={
                                            <NumberStepperInput
                                                name="extra_lead_time_max"
                                                ariaLabelMinus="Decrease maximum lead time"
                                                ariaLabelPlus="Increase maximum lead time"
                                                step={1}
                                                min={0}
                                                value={formValues.extra_lead_time_max}
                                                onChange={handleFieldChange}
                                                placeholder="2"
                                                rightLabel="days"
                                            />
                                        }
                                        valueIsNumberStepper={true}
                                    /> 

                                </>

                            }

                            <TwoColumnList
                                property={<label>Stock Used</label>}
                                value={
                                    <input  
                                        name="stock_used"
                                        type="number"
                                        min="0"
                                        placeholder="Stock Used"
                                        value={formValues.stock_used}
                                        onChange={handleFieldChange}
                                    />
                                }
                            />

                            <TwoColumnList
                                property={<label>Name</label>}
                                value={<input
                                    name="name"
                                    type="text"
                                    placeholder="Name"
                                    value={formValues.name}
                                    onChange={handleFieldChange}
                                    required
                                />}
                            />

                            <TwoColumnList
                                property={<label>Hex Colour</label>}
                                value={

                                    <div className="colourway-popup__colour-field">

                                        <input
                                            name="hex_colour"
                                            type="text"
                                            placeholder="#RRGGBB"
                                            value={formValues.hex_colour}
                                            onChange={handleFieldChange}
                                            required
                                        />

                                        <ColorPickerSmall
                                            value={formValues.hex_colour}
                                            onChange={handleColourChange}
                                            label=""
                                            title="Colourway colour"
                                            size="m"
                                            displayPickerLocationTopBottom="top"
                                            displayPickerLocationLeftRight="right"
                                        />

                                    </div>

                                }
                            />

                            <TwoColumnList
                                property={<label>Ordering</label>}
                                value={<input
                                    name="ordering"
                                    type="number"
                                    placeholder="Ordering"
                                    value={formValues.ordering}
                                    onChange={handleFieldChange}
                                />}
                            />
                           

                            <div className="colourway-popup__settings">

                                <p className="colourway-popup__settings-title">Settings</p>

                                {settingsRows.map((settingRow) => (

                                    <div key={settingRow.id} className="colourway-popup__settings-row">

                                        <input
                                            type="text"
                                            placeholder="Setting"
                                            value={settingRow.key}
                                            onChange={(event) => handleSettingChange(settingRow.id, "key", event.target.value)}
                                        />

                                        <input
                                            type="text"
                                            placeholder="Value"
                                            value={settingRow.value}
                                            onChange={(event) => handleSettingChange(settingRow.id, "value", event.target.value)}
                                        />

                                        <CreateButton
                                            type="button"
                                            tooltip="Remove Setting"
                                            size="s"
                                            icon="/images/icon_bin.png"
                                            iconTitle="Remove Setting"
                                            onClick={() => handleRemoveSettingRow(settingRow.id)}
                                        />

                                    </div>

                                ))}

                                <div className="colourway-popup__settings-actions">

                                    <CreateButton
                                        type="button"
                                        title="+ Add Setting"
                                        size="s"
                                        onClick={handleAddSettingRow}
                                    />

                                </div>

                            </div>

                            {errorMessage &&

                                <p className="colourway-popup__error">{errorMessage}</p>

                            }

                            <br/>
                            <br/>

                            {/* Footer actions */}
                            <div className="colourway-popup__footer">

                                {/* Show the permanent delete button only when editing an existing colourway */}
                                {isEditing &&

                                    <div className="colourway-popup__footer-left">

                                        <CreateButton
                                            type="button"
                                            title="Delete Colourway"
                                            tooltip="Delete Colourway"
                                            size="m"
                                            icon="/images/icon_bin.png"
                                            onClick={() => handleDeleteColourway()}
                                        />

                                    </div>

                                }

                                {/* Show the normal save and cancel buttons on the right */}
                                <div className="colourway-popup__footer-right">

                                    <CreateButton type="submit" title="Save" size="m" />

                                    <CreateButton type="button" title="Cancel" isCancel={true} invert size="m" onClick={onClose} />

                                </div>

                            </div>

                        </form>

                    </Grid>

                </div>

            </div>

        </div>

    );

};

export default RenderColourwayPopup;